import React, { useState, useEffect } from 'react';
import { Folder, Plus, Edit2, Trash2, Save, X, Tag, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const CategoriesManagement = () => {
  const { user, restaurants: userRestaurants } = useAuth();
  const [categories, setCategories] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [nameSearch, setNameSearch] = useState('');

  // API Base URL
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [formData, setFormData] = useState({
    restaurantId: '',
    name: '',
    nameEn: '',
    isRootCategory: true,
    parentCategoryId: '',
    description: '',
    displayOrder: 0,
    icon: '',
    isActive: true,
    image: '',
    imagePreview: '',
    originalFileName: '',
    selectedFile: null
  });

  // Load categories and restaurants from API
  useEffect(() => {
    const initializeRestaurants = async () => {
      console.log('CategoriesManagement - initializeRestaurants başladı');
      console.log('userRestaurants:', userRestaurants);
      console.log('user:', user);
      
      if (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) {
        console.log('userRestaurants var, length:', userRestaurants.length);
        setRestaurants(userRestaurants);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (userRestaurants.length === 1) {
          const restaurantId = userRestaurants[0].id || userRestaurants[0].restaurantId || userRestaurants[0].ID || userRestaurants[0].RestaurantId;
          console.log('Restaurant ID bulundu:', restaurantId, 'Restaurant objesi:', userRestaurants[0]);
          if (restaurantId) {
            // String'e çevir (select value string bekliyor)
            const restaurantIdStr = String(restaurantId);
            setFormData(prev => {
              console.log('Önceki formData.restaurantId:', prev.restaurantId);
              console.log('Yeni restaurantId:', restaurantIdStr);
              return {
                ...prev,
                restaurantId: restaurantIdStr
              };
            });
            console.log('Otomatik restoran seçildi (userRestaurants):', restaurantIdStr);
          } else {
            console.warn('Restaurant ID bulunamadı!');
          }
        }
      } else {
        console.log('userRestaurants yok veya boş, API\'den çekiliyor...');
        // userRestaurants yoksa veya boşsa API'den çek
        await fetchRestaurants();
      }
    };
    
    if (user) {
      initializeRestaurants();
    }
    fetchCategories();
  }, [userRestaurants, user]);

  // Debug: formData.restaurantId değişikliklerini izle
  useEffect(() => {
    console.log('formData.restaurantId güncellendi:', formData.restaurantId);
  }, [formData.restaurantId]);

  const fetchCategories = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Categories`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Categories/user/${user.id}`;
      }

      // Timeout ile fetch (30 saniye)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      try {
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        if (response.ok) {
          const data = await response.json();
          console.log('Categories fetched:', data);
          setCategories(data);
        } else {
          setError('Kategoriler yüklenirken hata oluştu');
        }
      } catch (fetchErr) {
        clearTimeout(timeoutId);
        if (fetchErr.name === 'AbortError') {
          setError('API yanıt vermedi (timeout). Lütfen tekrar deneyin.');
          console.error('API Timeout Error');
        } else {
          throw fetchErr;
        }
      }
    } catch (err) {
      if (err.name === 'TypeError' && err.message.includes('Failed to fetch')) {
        setError('API sunucusuna bağlanılamıyor. Lütfen bağlantınızı kontrol edin.');
      } else {
        setError('API bağlantı hatası');
      }
      console.error('API Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const fetchRestaurants = async () => {
    if (!user) return;
    
    try {
      const token = localStorage.getItem('authToken');
      let url = `${API_BASE_URL}/api/Restaurants`;
      
      // Convert numeric role to string for comparison
      const userRole = typeof user.role === 'number' ? 
        (user.role === 1 ? 'Admin' : user.role === 2 ? 'Manager' : user.role === 3 ? 'SuperAdmin' : 'User') : 
        user.role;
      
      // If user is not SuperAdmin, filter by their restaurant
      if (userRole !== 'SuperAdmin') {
        url = `${API_BASE_URL}/api/Restaurants/user/${user.id}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('Restaurants fetched:', data);
        setRestaurants(data);
        
        // Eğer sadece 1 restaurant varsa, onu otomatik seç
        if (Array.isArray(data) && data.length === 1) {
          const restaurantId = data[0].id || data[0].restaurantId || data[0].ID || data[0].RestaurantId;
          console.log('Restaurant ID bulundu (fetchRestaurants):', restaurantId, 'Restaurant objesi:', data[0]);
          if (restaurantId) {
            // String'e çevir (select value string bekliyor)
            const restaurantIdStr = String(restaurantId);
            setFormData(prev => {
              console.log('Önceki formData.restaurantId:', prev.restaurantId);
              console.log('Yeni restaurantId:', restaurantIdStr);
              return {
                ...prev,
                restaurantId: restaurantIdStr
              };
            });
            console.log('Otomatik restoran seçildi (fetchRestaurants):', restaurantIdStr);
          } else {
            console.warn('Restaurant ID bulunamadı (fetchRestaurants)!');
          }
        } else {
          console.log('Restaurant sayısı:', data.length, '- Otomatik seçim yapılmadı');
        }
      } else {
        console.error('Restoranlar yüklenirken hata oluştu');
      }
    } catch (err) {
      console.error('Restaurant API Error:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const nextValue = type === 'checkbox' ? checked : value;
      const nextForm = {
        ...prev,
        [name]: nextValue
      };

      if (name === 'isRootCategory' && checked) {
        nextForm.parentCategoryId = '';
      }

      return nextForm;
    });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Eski blob URL'ini temizle (memory leak önlemek için)
      setFormData(prev => {
        if (prev.imagePreview && prev.imagePreview.startsWith('blob:')) {
          URL.revokeObjectURL(prev.imagePreview);
        }
        return prev;
      });
      
      // Basit dosya adı oluştur
      const originalName = file.name;
      const extension = originalName.split('.').pop().toLowerCase();
      const baseName = originalName.split('.')[0].toLowerCase().replace(/[^a-z0-9]/g, '-');
      
      // Basit dosya adı (timestamp olmadan)
      const fileName = `${baseName}.${extension}`;
      const imagePath = `/MenuResim/${fileName}`;
      
      // Resim önizlemesi için URL oluştur
      const imageUrl = URL.createObjectURL(file);
      
      // Tek seferde tüm değerleri güncelle
      setFormData(prev => {
        const newData = {
          ...prev,
          image: imagePath,
          originalFileName: originalName,
          imagePreview: imageUrl, // Yeni seçilen resim için blob URL
          selectedFile: file // Dosyayı sakla
        };
        console.log('FormData güncelleniyor - imagePreview:', newData.imagePreview);
        return newData;
      });
      
      console.log('Image uploaded, preview URL:', imageUrl);
    }
  };

  const resetForm = () => {
    // Eğer tek restoran varsa, onu otomatik seç
    // Önce restaurants state'ini kontrol et, yoksa userRestaurants'ı kontrol et
    const availableRestaurants = (restaurants && Array.isArray(restaurants) && restaurants.length > 0) 
      ? restaurants 
      : (userRestaurants && Array.isArray(userRestaurants) && userRestaurants.length > 0) 
        ? userRestaurants 
        : [];
    
    let defaultRestaurantId = '';
    if (availableRestaurants.length === 1) {
      const restaurantId = availableRestaurants[0].id || availableRestaurants[0].restaurantId || availableRestaurants[0].ID || availableRestaurants[0].RestaurantId;
      if (restaurantId) {
        defaultRestaurantId = String(restaurantId);
        console.log('resetForm - Otomatik restoran seçildi:', defaultRestaurantId);
      }
    }
    
    setFormData({
      restaurantId: defaultRestaurantId,
      name: '',
      nameEn: '',
      isRootCategory: true,
      parentCategoryId: '',
      description: '',
      displayOrder: 0,
      icon: '',
      isActive: true,
      image: '',
      imagePreview: '',
      originalFileName: '',
      selectedFile: null
    });
  };

  const handleCreate = () => {
    setIsCreating(true);
    resetForm();
  };

  const handleEdit = (category) => {
    console.log('Editing category:', category);
    
    // Try different possible ID field names
    const possibleIds = [
      category.categoryId,
      category.id, 
      category.CategoryId,
      category.ID
    ];
    
    // Find the first non-null ID
    const actualId = possibleIds.find(id => id != null);
    console.log('Using category ID:', actualId);
    
    if (!actualId) {
      setError('Category ID bulunamadı');
      return;
    }
    
    setError('');
    setIsCreating(false);
    setEditingCategory(actualId);
    
    // Image path'i kontrol et - zaten tam URL ise direkt kullan, değilse API_BASE_URL ekle
    const imagePath = category.image || '';
    const imagePreviewUrl = imagePath 
      ? (imagePath.startsWith('http') 
          ? imagePath 
          : `${API_BASE_URL}${imagePath.startsWith('/') ? imagePath : '/' + imagePath}`)
      : '';
    
    setFormData({
      restaurantId: category.restaurantId || '',
      name: category.name || '',
      nameEn: category.englishName || category.EnglishName || '',
      isRootCategory: !(category.parentCategoryId || category.ParentCategoryId),
      parentCategoryId: category.parentCategoryId || category.ParentCategoryId || '',
      description: category.description || '',
      displayOrder: category.displayOrder || 0,
      icon: category.icon || '',
      isActive: category.isActive !== undefined ? category.isActive : true,
      image: imagePath,
      imagePreview: imagePreviewUrl,
      originalFileName: imagePath ? imagePath.split('/').pop() : '',
      selectedFile: null
    });
    
    console.log('Form data set for editing:', {
      image: imagePath,
      imagePreview: imagePreviewUrl,
      categoryImage: category.image
    });

    // Guncelleme formu ust tarafta oldugu icin otomatik yukari kaydir
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      // Validation
      if (!formData.name.trim()) {
        setError('Kategori adı zorunludur');
        return;
      }

      if (!formData.restaurantId.trim()) {
        setError('Restoran ID zorunludur');
        return;
      }

      // FormData oluştur (dosya yükleme için) - ProductsManagement'teki gibi her zaman FormData kullan
      const formDataToSend = new FormData();
      
      // Temel verileri ekle - Sadece DTO'daki alanlar
      if (editingCategory) {
        formDataToSend.append('id', editingCategory);
      }
      formDataToSend.append('restaurantId', formData.restaurantId.trim());
      formDataToSend.append('name', formData.name.trim());
      formDataToSend.append('englishName', (formData.nameEn || '').trim());
      if (!formData.isRootCategory && formData.parentCategoryId) {
        formDataToSend.append('parentCategoryId', formData.parentCategoryId);
      }
      formDataToSend.append('description', formData.description.trim() || '');
      formDataToSend.append('displayOrder', (parseInt(formData.displayOrder) || 0).toString());
      formDataToSend.append('icon', formData.icon.trim() || '');
      formDataToSend.append('isActive', formData.isActive.toString());
      formDataToSend.append('image', formData.image.trim() || '');
      
      // Dosya varsa ekle
      if (formData.selectedFile) {
        formDataToSend.append('imageFile', formData.selectedFile);
      }

      console.log('FormData hazırlandı:');
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value);
      }

      let response;
      
      const token = localStorage.getItem('authToken');
      console.log('Token alındı:', token ? 'Token var' : 'Token yok');
      
      if (editingCategory) {
        // Update existing category
        console.log('UPDATE işlemi başlıyor...');
        console.log('URL:', `${API_BASE_URL}/api/Categories/${editingCategory}`);
        console.log('Method: PUT');
        console.log('Headers:', {
          'Authorization': `Bearer ${token}`,
        });
        console.log('Body: FormData (multipart/form-data)');
        
        response = await fetch(`${API_BASE_URL}/api/Categories/${editingCategory}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
          },
          body: formDataToSend
        });
      } else {
        // Create new category
        console.log('CREATE işlemi başlıyor...');
        console.log('URL:', `${API_BASE_URL}/api/Categories`);
        console.log('Method: POST');
        console.log('Headers:', {
          'Authorization': `Bearer ${token}`,
        });
        console.log('Body: FormData (multipart/form-data)');
        
        response = await fetch(`${API_BASE_URL}/api/Categories`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            // Content-Type header'ını kaldır çünkü FormData otomatik olarak multipart/form-data olarak gönderilir
          },
          body: formDataToSend
        });
      }

      console.log('Response alındı:', response);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (response.ok) {
        console.log('Response başarılı!');
        const responseData = await response.text();
        console.log('Response data:', responseData);
        
        await fetchCategories();
        setIsCreating(false);
        setEditingCategory(null);
        resetForm();
        console.log('İşlem tamamlandı');
      } else if (response.status === 500) {
        // Check if it's the EF Core trigger issue
        const errorText = await response.text();
        console.log('Error Response:', errorText);
        
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          console.warn('EF Core trigger issue detected, checking if data was actually saved...');
          
          setTimeout(async () => {
            await fetchCategories();
            
            const savedCategory = categories.find(c => c.name === formData.name.trim() && c.restaurantId === formData.restaurantId.trim());
            if (savedCategory) {
              console.log('Data was saved successfully despite the error!');
              setIsCreating(false);
              setEditingCategory(null);
              resetForm();
              setError('');
            } else {
              setError(`EF Core Hatası: ${errorText}`);
            }
          }, 1000);
        } else {
          setError(`Server Hatası (500): ${errorText}`);
        }
      } else if (response.status === 400) {
        // Bad Request - usually validation error
        const errorText = await response.text();
        console.error('400 Bad Request Error:', errorText);
        
        try {
          const errorJson = JSON.parse(errorText);
          console.error('Parsed error:', errorJson);
          
          // Check for validation errors
          if (errorJson.errors) {
            const validationErrors = Object.entries(errorJson.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('; ');
            setError(`Validation Hatası: ${validationErrors}`);
          } else if (errorJson.title) {
            setError(`API Hatası: ${errorJson.title}`);
          } else {
            setError(`Bad Request: ${errorText}`);
          }
        } catch {
          setError(`Bad Request (400): ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        setError(`API Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
      }
    } catch (err) {
      console.error('Network/Request Error:', err);
      setError(`Bağlantı hatası: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsCreating(false);
    setEditingCategory(null);
    resetForm();
    setError('');
  };

  const handleDelete = async (categoryId) => {
    console.log('handleDelete called with ID:', categoryId);
    
    if (window.confirm('Bu kategoriyi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.')) {
      try {
        // Get the category to be deleted for verification
        const categoryToDelete = categories.find(c => {
          const possibleIds = [c.categoryId, c.id, c.CategoryId, c.ID];
          return possibleIds.includes(categoryId);
        });
        
        if (!categoryToDelete) {
          setError('Category bulunamadı');
          return;
        }
        
        const actualId = [categoryToDelete.categoryId, categoryToDelete.id, categoryToDelete.CategoryId, categoryToDelete.ID]
          .find(id => id != null);
        
        console.log('Deleting category with ID:', actualId);
        
        const response = await fetch(`${API_BASE_URL}/api/Categories/${actualId}`, {
          method: 'DELETE',
        });

        console.log('Delete Response Status:', response.status);

        if (response.ok) {
          console.log('Delete successful!');
          await fetchCategories();
        } else if (response.status === 500) {
          const errorText = await response.text();
          if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
            console.warn('EF Core trigger issue detected during delete');
            setTimeout(async () => {
              await fetchCategories();
              setError('');
            }, 1000);
          } else if (errorText.includes('REFERENCE constraint') || errorText.includes('FK_')) {
            setError('Bu kategori silinemez çünkü bağlı ürünleri var. Önce ürünleri silmeniz gerekiyor.');
          } else {
            setError(`Server Hatası (500): ${errorText}`);
          }
        } else {
          const errorText = await response.text();
          setError(`Silme Hatası (${response.status}): ${errorText || 'Bilinmeyen hata'}`);
        }
      } catch (err) {
        setError(`Bağlantı hatası: ${err.message}`);
        console.error('Delete Error:', err);
      }
    }
  };

  const toggleCategoryStatus = async (category) => {
    try {
      // Get the actual ID
      const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
      const actualId = possibleIds.find(id => id != null);
      
      if (!actualId) {
        setError('Category ID bulunamadı');
        return;
      }

      const updatedData = {
        restaurantId: category.restaurantId,
        name: category.name,
        englishName: category.englishName || category.EnglishName || '',
        parentCategoryId: category.parentCategoryId || category.ParentCategoryId || null,
        description: category.description || null,
        displayOrder: category.displayOrder || 0,
        icon: category.icon || null,
        isActive: !category.isActive
      };

      console.log('Toggling category status:', actualId, 'New status:', !category.isActive);

      const response = await fetch(`${API_BASE_URL}/api/Categories/${actualId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedData)
      });

      if (response.ok) {
        await fetchCategories();
      } else if (response.status === 500) {
        const errorText = await response.text();
        if (errorText.includes('database triggers') || errorText.includes('OUTPUT clause')) {
          setTimeout(async () => {
            await fetchCategories();
            setError('');
          }, 1000);
        } else {
          setError(`Durum güncellenirken hata oluştu: ${errorText}`);
        }
      } else {
        const errorText = await response.text();
        setError(`Durum güncellenirken hata oluştu: ${errorText}`);
      }
    } catch (err) {
      setError('API bağlantı hatası');
      console.error('Toggle Error:', err);
    }
  };

  const getRestaurantName = (restaurantId) => {
    const restaurant = restaurants.find(r => {
      const possibleIds = [r.restaurantId, r.id, r.RestaurantId, r.ID];
      return possibleIds.includes(restaurantId);
    });
    return restaurant ? restaurant.name : restaurantId;
  };

  const getCategoryNameById = (categoryId) => {
    if (!categoryId) return '';
    const category = categories.find(c => {
      const cid = c.categoryId || c.id || c.CategoryId || c.ID;
      return String(cid) === String(categoryId);
    });
    return category?.name || '';
  };

  const getRestaurantId = (restaurant) => {
    const possibleIds = [restaurant.restaurantId, restaurant.id, restaurant.RestaurantId, restaurant.ID];
    const id = possibleIds.find(id => id != null);
    // String'e çevir (select value string bekliyor)
    return id != null ? String(id) : null;
  };

  const getAvailableParentCategories = () => {
    if (!formData.restaurantId) return [];

    return categories.filter(category => {
      const categoryRestaurantId = String(category.restaurantId || category.RestaurantId || '');
      const categoryId = category.categoryId || category.id || category.CategoryId || category.ID;
      const editingId = editingCategory ? String(editingCategory) : null;

      return categoryRestaurantId === String(formData.restaurantId) && String(categoryId) !== editingId;
    });
  };

  const getFilteredAndSortedCategories = () => {
    const normalizedSearch = nameSearch.trim().toLowerCase();

    const filtered = categories.filter(category => {
      const isChild = !!(category.parentCategoryId || category.ParentCategoryId);
      const categoryName = String(category.name || '').toLowerCase();

      const matchesType =
        typeFilter === 'all' ||
        (typeFilter === 'root' && !isChild) ||
        (typeFilter === 'child' && isChild);

      const matchesSearch = !normalizedSearch || categoryName.includes(normalizedSearch);

      return matchesType && matchesSearch;
    });

    return filtered.sort((a, b) => {
      const aIsChild = !!(a.parentCategoryId || a.ParentCategoryId);
      const bIsChild = !!(b.parentCategoryId || b.ParentCategoryId);

      if (aIsChild !== bIsChild) {
        return aIsChild ? 1 : -1; // Ana kategoriler ustte
      }

      const aOrder = a.displayOrder || 0;
      const bOrder = b.displayOrder || 0;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const aName = String(a.name || '');
      const bName = String(b.name || '');
      return aName.localeCompare(bName, 'tr');
    });
  };

  const displayedCategories = getFilteredAndSortedCategories();

  // Icon kodunu emoji'ye çevir
  const getIconEmoji = (iconCode) => {
    const iconMap = {
      'fa-pizza-slice': '🍕',
      'fa-hamburger': '🍔',
      'fa-leaf': '🥗',
      'fa-utensils': '🍜',
      'fa-fish': '🍣',
      'fa-drumstick-bite': '🥩',
      'fa-bread-slice': '🥪',
      'fa-taco': '🌮',
      'fa-french-fries': '🍟',
      'fa-cake-candles': '🍰',
      'fa-ice-cream': '🍦',
      'fa-mug-hot': '☕',
      'fa-beer-mug-empty': '🍺',
      'fa-wine-glass': '🍷',
      'fa-bottle-water': '🥤',
      'fa-apple-whole': '🧃',
      'fa-smoking': '💨',
      'fa-cookie-bite': '🍪',
      'fa-circle': '🍩',
      'fa-candy-cane': '🍫',
      'fa-seedling': '🥜',
      'fa-carrot': '🥕',
      'fa-cheese': '🧀',
      'fa-egg': '🥚',
      'fa-square': '🧇',
      'fa-bacon': '🥓',
      'fa-hotdog': '🌭'
    };
    return iconMap[iconCode] || iconCode || '🍽️';
  };

  return (
    <div className="max-w-6xl mx-auto p-6 bg-gray-50 min-h-screen">
      <div className="bg-white rounded-lg shadow-sm">
        {/* Header */}
        <div className="border-b border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Folder className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Kategori Yönetimi</h1>
                <p className="text-gray-600">Menü kategorilerini ekleyin ve düzenleyin</p>
              </div>
            </div>
            {!isCreating && !editingCategory && (
              <button
                onClick={handleCreate}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Yeni Kategori</span>
              </button>
            )}
          </div>
        </div>

        {/* Form */}
        {(isCreating || editingCategory) && (
          <div className="border-b border-gray-200 p-6 bg-gray-50">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              {editingCategory ? 'Kategoriyi Düzenle' : 'Yeni Kategori Ekle'}
            </h2>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Restoran *
                </label>
                <select
                  name="restaurantId"
                  value={formData.restaurantId}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Restoran seçiniz</option>
                  {restaurants.map((restaurant) => {
                    const restaurantId = getRestaurantId(restaurant);
                    return (
                      <option key={restaurantId} value={restaurantId}>
                        {restaurant.name}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori Adı *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Kategori adını girin"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İngilizce Kategori Adı (Opsiyonel)
                </label>
                <input
                  type="text"
                  name="nameEn"
                  value={formData.nameEn}
                  onChange={handleInputChange}
                  placeholder="English category name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isRootCategory"
                    name="isRootCategory"
                    checked={formData.isRootCategory}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isRootCategory" className="text-sm font-medium text-gray-700">
                    Ana kategori olarak kaydet
                  </label>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Açıklama
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  placeholder="Kategori açıklaması"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ana Kategori (Opsiyonel)
                </label>
                <select
                  name="parentCategoryId"
                  value={formData.parentCategoryId}
                  onChange={handleInputChange}
                  disabled={formData.isRootCategory}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">Ana kategori yok</option>
                  {getAvailableParentCategories().map((category) => {
                    const categoryId = category.categoryId || category.id || category.CategoryId || category.ID;
                    return (
                      <option key={categoryId} value={categoryId}>
                        {category.name}
                      </option>
                    );
                  })}
                </select>
                {formData.isRootCategory && (
                  <p className="text-xs text-gray-500 mt-1">Ana kategori secili oldugu icin ust kategori secimi kapatildi.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sıralama
                </label>
                <input
                  type="number"
                  name="displayOrder"
                  value={formData.displayOrder}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  İkon
                </label>
                <select
                  name="icon"
                  value={formData.icon}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                >
                  <option value="">İkon seçin</option>
                  <option value="fa-pizza-slice">🍕 Pizza</option>
                  <option value="fa-hamburger">🍔 Burger</option>
                  <option value="fa-leaf">🥗 Salata</option>
                  <option value="fa-utensils">🍜 Noodle</option>
                  <option value="fa-fish">🍣 Sushi</option>
                  <option value="fa-drumstick-bite">🥩 Et</option>
                  <option value="fa-drumstick-bite">🍗 Tavuk</option>
                  <option value="fa-fish">🐟 Balık</option>
                  <option value="fa-bread-slice">🥪 Sandviç</option>
                  <option value="fa-taco">🌮 Taco</option>
                  <option value="fa-french-fries">🍟 Patates</option>
                  <option value="fa-cake-candles">🍰 Tatlı</option>
                  <option value="fa-ice-cream">🍦 Dondurma</option>
                  <option value="fa-mug-hot">☕ İçecek</option>
                  <option value="fa-beer-mug-empty">🍺 Bira</option>
                  <option value="fa-wine-glass">🍷 Şarap</option>
                  <option value="fa-bottle-water">🥤 Gazlı İçecek</option>
                  <option value="fa-apple-whole">🧃 Meyve Suyu</option>
                  <option value="fa-mug-hot">🍵 Çay</option>
                  <option value="fa-bottle-water">🥛 Süt</option>
                  <option value="fa-smoking">💨 Nargile</option>
                  <option value="fa-cookie-bite">🍪 Kurabiye</option>
                  <option value="fa-circle">🍩 Donut</option>
                  <option value="fa-candy-cane">🍫 Çikolata</option>
                  <option value="fa-candy-cane">🍭 Şeker</option>
                  <option value="fa-circle">🍬 Bonbon</option>
                  <option value="fa-bottle-water">🍯 Bal</option>
                  <option value="fa-seedling">🥜 Fındık</option>
                  <option value="fa-seedling">🌰 Kestane</option>
                  <option value="fa-seedling">🥑 Avokado</option>
                  <option value="fa-seedling">🥦 Brokoli</option>
                  <option value="fa-carrot">🥕 Havuç</option>
                  <option value="fa-apple-whole">🍅 Domates</option>
                  <option value="fa-leaf">🥬 Marul</option>
                  <option value="fa-cheese">🧀 Peynir</option>
                  <option value="fa-egg">🥚 Yumurta</option>
                  <option value="fa-bread-slice">🥖 Ekmek</option>
                  <option value="fa-bread-slice">🍞 Çörek</option>
                  <option value="fa-bread-slice">🥐 Kruvasan</option>
                  <option value="fa-circle">🥨 Simit</option>
                  <option value="fa-circle">🥯 Bagel</option>
                  <option value="fa-egg">🍳 Omlet</option>
                  <option value="fa-circle">🥞 Pancake</option>
                  <option value="fa-square">🧇 Waffle</option>
                  <option value="fa-bacon">🥓 Pastırma</option>
                  <option value="fa-hotdog">🌭 Hot Dog</option>
                  <option value="fa-seedling">🍿 Patlamış Mısır</option>
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Kategori Resmi
                </label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  {(formData.imagePreview || formData.image) && (
                    <div className="mt-2">
                      <p className="text-sm text-gray-600 mb-2">Önizleme:</p>
                      <img
                        key={`preview-${formData.imagePreview || formData.image || 'none'}`} // Key ekle ki React yeniden render etsin
                        src={
                          formData.imagePreview && formData.imagePreview.startsWith('blob:') 
                            ? formData.imagePreview // Yeni seçilen resim (blob URL)
                            : formData.imagePreview 
                              ? formData.imagePreview // Mevcut resim (zaten tam URL olarak ayarlanmış)
                              : formData.image 
                                ? (formData.image.startsWith('http') 
                                    ? formData.image // Zaten tam URL
                                    : `${API_BASE_URL}${formData.image}`) // Path ise API URL'yi ekle
                                : ''
                        }
                        alt="Kategori önizleme"
                        className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                        onError={(e) => {
                          console.error('Resim yüklenemedi:', e.target.src);
                          e.target.style.display = 'none';
                        }}
                        onLoad={() => {
                          console.log('Resim yüklendi - Preview:', formData.imagePreview, 'Image:', formData.image);
                        }}
                      />
                    </div>
                  )}
                  {formData.originalFileName && (
                    <p className="text-xs text-gray-500">
                      Seçilen dosya: {formData.originalFileName}
                    </p>
                  )}
                </div>
              </div>

              <div className="md:col-span-2">
                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    id="isActive"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                    Kategori aktif
                  </label>
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3 mt-6">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400"
              >
                <Save className="h-4 w-4" />
                <span>{loading ? 'Kaydediliyor...' : 'Kaydet'}</span>
              </button>
              <button
                onClick={handleCancel}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <X className="h-4 w-4" />
                <span>İptal</span>
              </button>
            </div>
          </div>
        )}

        {/* Categories List */}
        <div className="p-6">
          <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <input
                type="text"
                value={nameSearch}
                onChange={(e) => setNameSearch(e.target.value)}
                placeholder="Kategori adina gore ara..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="all">Tum Kategoriler</option>
                <option value="root">Ana Kategori</option>
                <option value="child">Alt Kategori</option>
              </select>
            </div>
          </div>

          {loading && categories.length === 0 ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Yükleniyor...</p>
            </div>
          ) : displayedCategories.length === 0 ? (
            <div className="text-center py-12">
              <Folder className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Sonuc bulunamadi</h3>
              <p className="text-gray-600 mb-4">Filtre veya arama kriterlerini degistirin</p>
              <button
                onClick={handleCreate}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
              >
                Kategori Ekle
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {displayedCategories.map((category) => (
                <div key={category.categoryId || category.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                                            {category.icon && (
                      <span className="text-2xl">{getIconEmoji(category.icon)}</span>
                    )}
                        <Tag className="h-4 w-4 text-green-500" />
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => toggleCategoryStatus(category)}
                          className={`p-1 rounded-full ${category.isActive ? 'bg-green-500' : 'bg-gray-500'} text-white`}
                        >
                          {category.isActive ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 text-gray-400 hover:text-green-600 transition-colors"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => {
                          const possibleIds = [category.categoryId, category.id, category.CategoryId, category.ID];
                          const actualId = possibleIds.find(id => id != null);
                          if (actualId) {
                            handleDelete(actualId);
                          } else {
                            setError('Category ID bulunamadı');
                          }
                        }}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <h3 className="font-bold text-gray-900">{category.name}</h3>
                    
                    {category.description && (
                      <p className="text-gray-600 text-sm">{category.description}</p>
                    )}
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">
                        Sıralama: {category.displayOrder || 0}
                      </span>
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        category.isActive 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {category.isActive ? 'Aktif' : 'Pasif'}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 space-y-1">
                      <div>Restoran: {getRestaurantName(category.restaurantId)}</div>
                      <div>Tip: {(category.parentCategoryId || category.ParentCategoryId) ? 'Alt Kategori' : 'Ana Kategori'}</div>
                      {(category.parentCategoryId || category.ParentCategoryId) && (
                        <div>Ana Kategori: {getCategoryNameById(category.parentCategoryId || category.ParentCategoryId)}</div>
                      )}
                      {category.createdDate && (
                        <div>Oluşturulma: {formatDate(category.createdDate)}</div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagement;